import { useSyncExternalStore } from 'react';

// State shared between the custom CSS editor and the form it describes. The
//  editor lives in the styles side panel and the form is rendered by Design,
//  which is a sibling rather than an ancestor, so this sits outside React
//  where both can see it. A plain event would be missed by whichever of the
//  two mounts second.
const state = {
    // The editor is on screen, so hovering the form names selectors
    isEditorVisible: false,
    // The inspector is mounted, so there is actually a form to point at
    isInspectorReady: false,
    // Picking: the next click in the form is a choice, not a click through
    isPicking: false,
};

let onPicked = null;
const listeners = new Set();

function subscribe(listener) {
    listeners.add(listener);

    return () => listeners.delete(listener);
}

function update(changes) {
    const changed = Object.keys(changes).some(
        key => state[key] !== changes[key],
    );

    if (!changed) {
        return;
    }

    Object.assign(state, changes);
    listeners.forEach(listener => listener());
}

export function setCssEditorVisible(isEditorVisible) {
    update({ isEditorVisible });

    if (!isEditorVisible) {
        stopPickingElement();
    }
}

export function setInspectorReady(isInspectorReady) {
    update({ isInspectorReady });

    if (!isInspectorReady) {
        stopPickingElement();
    }
}

/**
 * @param {(selectors: {selector: string, inQuestionType?: string, inQuestion?: string}) => void} handler
 *  called with every selector that reaches the chosen element, broadest first
 */
export function startPickingElement(handler) {
    onPicked = handler;
    update({ isPicking: true });
}

export function stopPickingElement() {
    onPicked = null;
    update({ isPicking: false });
}

export function completePick(selectors) {
    const handler = onPicked;

    stopPickingElement();
    handler?.(selectors);
}

function getState() {
    return state;
}

export function useIsCssEditorVisible() {
    return useSyncExternalStore(
        subscribe,
        () => getState().isEditorVisible,
        () => false,
    );
}

export function useIsInspectorReady() {
    return useSyncExternalStore(
        subscribe,
        () => getState().isInspectorReady,
        () => false,
    );
}

export function useIsPickingElement() {
    return useSyncExternalStore(
        subscribe,
        () => getState().isPicking,
        () => false,
    );
}
